import { getAxiosErrorMessage } from '@helpers/axios'

import { useToast } from '@UI/Toast/ToastProvider'

export interface ApiResponse {
  success: boolean
  message: string
  data: any
}

export interface ApiCallbacks<T> {
  onStart?: () => void
  onSuccess?: (value: T | null) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

interface RunApiGetOptions<T> {
  setLoading: (loading: boolean) => void
  handle: (response: ApiResponse) => T | null
  hooks?: ApiCallbacks<T>
}

export const runApiGet = async <T>(
  url: string | null,
  options: RunApiGetOptions<T>,
): Promise<T | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const setError = (message: string): null => {
    options.hooks?.onError?.(message)
    toast.error(message)
    options.setLoading(false)
    return null
  }

  options.hooks?.onStart?.()
  options.setLoading(true)

  try {
    if (!url) return setError('Unexpected error. Invalid request URL.')

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) return setError(data.message)

    const value = options.handle(data)
    options.hooks?.onSuccess?.(value)
    options.setLoading(false)

    return value
  } catch (err: unknown) {
    return setError(getAxiosErrorMessage(err))
  } finally {
    options.hooks?.onFinish?.()
  }
}
